import { SettingsFormHeader } from "@babackapp/react-hook-forms-components/form-page";
import useRestoreReturnReason from "@babackapp/private-api-client/client/use-restore-return-reason";
import type { ShopReasonsDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Card, CardContent } from "@babackapp/ui/components/card";
import { ArchiveIcon, ResetIcon } from "@radix-ui/react-icons";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import useTextDtoValue from "@/utils/use-text-dto-value";

export default function ArchivedReturnReasonList() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const { mutate: restoreReason, isPending } = useRestoreReturnReason(shop);

  const archivedReasons = shop.reasons.filter(
    (reason) =>
      !!reason.archivedAt && reason.babackStandardReason !== "RETRACTION",
  );

  if (archivedReasons.length === 0) return null;

  return (
    <Card>
      <CardContent className="py-8">
        <SettingsFormHeader
          title={t("settings.reasons.archived.title")}
          description={t("settings.reasons.archived.subtitle")}
        />
        <div className="mt-2 flex flex-col">
          {archivedReasons.map((reason) => (
            <ArchivedReturnReasonListItem
              key={reason.id}
              reason={reason}
              disabled={isPending}
              onRestore={() => reason.id && restoreReason(reason.id)}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

type ArchivedReturnReasonListItemProps = {
  reason: ShopReasonsDto;
  disabled: boolean;
  onRestore: () => void;
};

function ArchivedReturnReasonListItem({
  reason,
  disabled,
  onRestore,
}: ArchivedReturnReasonListItemProps) {
  const t = useTranslation();
  const label = useTextDtoValue(reason.values);

  return (
    <div className="flex flex-row items-center justify-between gap-4 border-b border-border px-2 py-3 last:border-b-0">
      <div className="flex flex-row items-center gap-4">
        <ArchiveIcon className="size-4 text-muted-foreground" />
        <p className="text-base font-medium leading-none tracking-tight text-muted-foreground">
          {label}
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        type="button"
        disabled={disabled}
        onClick={onRestore}
      >
        <ResetIcon className="mr-2 size-4" />
        {t("settings.reasons.archived.restore")}
      </Button>
    </div>
  );
}
